import type { PropsWithChildren } from 'react';

import { DreamComponentsProvider } from '@shared/dream-components';

import { getAppGlobals } from '~/components/AppGlobals/utils';
import DefaultPremiumGate from '~/components/DefaultPremiumGate';
import usePremiumPageGate from '~/hooks/usePremiumPageGate';
import useSignupRedirects from '~/hooks/useUtils/useSignupRedirects';
import type { Page } from '~/interfaces/page';
import type { Site } from '~/interfaces/site';
import SuspendedPage from '~/routes/subscribe/$subscriberId/_components/v2/SuspendedPage';

import { PageSerializer } from '../DreamBuilder';
import FooterSerializer from '../DreamBuilder/FooterSerializer';
import GlobalElementsSerializer from '../DreamBuilder/GlobalElementsSerializer';
import NavbarSerializerV2 from '../DreamBuilder/NavbarSerializerV2';
import SearchModal from '../DreamBuilder/nodes/SearchModal/SearchModal';

type Props = PropsWithChildren<{
  site: Site;
  page?: Page;
  forceShowNavbar?: boolean;
  forceShowFooter?: boolean;
  isSearchModalEnabled?: boolean;
}>;

const StaticPage = ({ page, site, children, forceShowNavbar, forceShowFooter, isSearchModalEnabled = true }: Props) => {
  const { isInProgress, showNavigation } = useSignupRedirects();
  const { isPremiumGated, hasPremiumAccess, isFetchingSubscription, subscription } = usePremiumPageGate(page, site);

  if (!site) {
    // show the refresh page message if we didn't get loader data
    throw new Error('RefreshPageError');
  }

  if (site.publication.website_suspended) {
    return <SuspendedPage site={site} />;
  }

  // Using !== false to check if explicitly set to false
  // New pages by default have empty customization
  // When showNavigation is enabled on the signup flow, keep showing navbar/footer during the flow
  const hideForSignupFlow = isInProgress && !showNavigation;
  const showNavbar =
    forceShowNavbar === true ||
    (page?.viewable_page_version?.customization?.show_navbar !== false && !hideForSignupFlow);
  const showFooter =
    forceShowFooter === true ||
    (page?.viewable_page_version?.customization?.show_footer !== false && !hideForSignupFlow);

  const appGlobals = getAppGlobals();
  const externalEmbedBaseDomain = appGlobals.VITE_EXTERNAL_EMBED_DOMAIN || '';
  if (import.meta.env.PROD && externalEmbedBaseDomain === '') {
    throw new Error('VITE_EXTERNAL_EMBED_DOMAIN should have a valid domain');
  }

  // Build full external embed URL with publication-scoped subdomain (djb2 hash for sync SSR)
  const getExternalEmbedUrl = (): string => {
    if (!externalEmbedBaseDomain) return '';
    let hostname = new URL(site.publication.url).hostname.toLowerCase();
    if (hostname.startsWith('www.')) {
      hostname = hostname.slice(4);
    }
    // Simple djb2 hash for sync SSR
    let hash = 5381;
    for (let i = 0; i < hostname.length; i++) {
      hash = (hash * 33) ^ hostname.charCodeAt(i);
    }
    const subdomain = Math.abs(hash).toString(36);
    return `https://${subdomain}.${externalEmbedBaseDomain}`;
  };

  const externalEmbedUrl = getExternalEmbedUrl();

  const renderPageContent = () => {
    if (isPremiumGated && isFetchingSubscription) {
      return null;
    }

    if (isPremiumGated && !hasPremiumAccess && page?.paywall) {
      return (
        <DefaultPremiumGate paywall={page.paywall} subscription={subscription} primaryTheme={site.primary_theme} />
      );
    }

    return (
      <>
        {page && <PageSerializer site={site} page={page} isFooterVisible={showFooter} />}
        {children}
      </>
    );
  };

  return (
    <DreamComponentsProvider
      context={{
        responsiveImagesEnabled: true,
        imagePriorityThreshold: 5,
        publicationTimeZone: site.publication.time_zone,
        externalEmbedEnabled: site.publication.has_external_embed,
        externalEmbedUrl,
      }}
    >
      {showNavbar && <NavbarSerializerV2 site={site} page={page} />}
      {renderPageContent()}
      {showFooter && <FooterSerializer site={site} />}
      <GlobalElementsSerializer page={page} site={site} />
      {isSearchModalEnabled && <SearchModal />}
    </DreamComponentsProvider>
  );
};

export default StaticPage;
